import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { themeAttribute, uiClass } from "./appearance.js";
const focusable = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
/**
 * Accessible modal shell with focus management and a dismissible backdrop.
 *
 * When open, it moves focus to the first control, traps Tab navigation, closes on Escape, prevents
 * page scrolling, and restores the element that was focused previously.
 */
export function Modal({ open, title, onClose, children, className, style, theme, unstyled }) {
    const dialog = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (!open)
            return;
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
        const frame = window.requestAnimationFrame(() => {
            dialog.current?.querySelector(focusable)?.focus();
        });
        const onKey = (e) => {
            if (e.key === "Escape") {
                onCloseRef.current();
                return;
            }
            if (e.key !== "Tab" || !dialog.current)
                return;
            const items = [...dialog.current.querySelectorAll(focusable)];
            const first = items[0];
            const last = items.at(-1);
            if (!first || !last)
                return;
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            window.cancelAnimationFrame(frame);
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
            previousFocus?.focus();
        };
    }, [open]);
    if (!open)
        return null;
    return (_jsxs("div", { className: uiClass("kui-root", unstyled, className), style: style, "data-kui": "modal", "data-theme": themeAttribute(theme), "data-state": "open", children: [_jsx("button", { type: "button", className: uiClass("kui-backdrop", unstyled), "data-kui-slot": "backdrop", "aria-label": "Close", onClick: onClose }), _jsx("div", { ref: dialog, className: uiClass("kui-dialog", unstyled), "data-kui-slot": "dialog", role: "dialog", "aria-modal": "true", "aria-label": title, children: children })] }));
}
//# sourceMappingURL=Modal.js.map